import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';

import { useConsultarChave } from '@/lib/captura/api';
import {
  mensagemErroCaptura,
  rotaDocumento,
  temNota,
  TITULO_RESULTADO_CHAVE,
  TOM_RESULTADO_CHAVE,
  type TomResultado,
} from '@/lib/captura/textos';
import { cx } from '@/lib/cx';
import { BotaoComEspera } from '@/ui/components/botao-com-espera';
import { CampoChaveAcesso } from '@/ui/components/campo-chave-acesso';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { estiloBotao } from '@/ui/primitives/estilos';
import { Campo } from '@/ui/primitives/input';
import {
  type ConsultaChaveResposta,
  consultaChaveRequestSchema,
  type EstadoCaptura,
} from '@fiscoai/contracts';

const QUADRO: Record<TomResultado, string> = {
  sucesso: 'border-sucesso/40 bg-sucesso-suave [&_svg]:text-sucesso',
  info: 'border-info/40 bg-info-suave [&_svg]:text-info',
  alerta: 'border-alerta/40 bg-alerta-suave [&_svg]:text-alerta',
  perigo: 'border-perigo/40 bg-perigo-suave [&_svg]:text-perigo',
};
const ICONE = { sucesso: CircleCheck, info: Info, alerta: TriangleAlert, perigo: CircleAlert };

interface Retorno {
  tom: TomResultado;
  titulo: string;
  mensagem: string;
  resposta?: ConsultaChaveResposta;
}

function Resultado({ r }: { r: Retorno }) {
  const Icone = ICONE[r.tom];
  const resposta = r.resposta;
  return (
    <div
      role="status"
      className={cx(
        'flex items-start gap-3 rounded-cartao border px-4 py-3 animate-surgir',
        QUADRO[r.tom],
      )}
    >
      <Icone className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="grid min-w-0 flex-1 gap-1 text-sm">
        <p className="font-semibold">{r.titulo}</p>
        <p className="text-texto-suave">{r.mensagem}</p>
        {resposta?.cienciaEnviada && (
          <p className="text-texto-suave">Ciência enviada: o XML chega na próxima consulta.</p>
        )}
      </div>
      {resposta && temNota(resposta) && (
        <Link to={rotaDocumento(resposta.documentoId)} className={estiloBotao({ tamanho: 'sm' })}>
          Abrir nota
        </Link>
      )}
    </div>
  );
}

/** "Buscar nota pela chave" (consChNFe): valida o DV antes de gastar uma das 10 consultas da hora. */
export function DialogoConsultaChave({
  estado,
  aoFechar,
}: {
  estado: EstadoCaptura;
  aoFechar: () => void;
}) {
  const idForm = useId();
  const [chave, setChave] = useState('');
  const [erro, setErro] = useState<string | undefined>();
  const [retorno, setRetorno] = useState<Retorno | null>(null);
  const consultar = useConsultarChave(estado.empresa.id);
  const usadas = retorno?.resposta?.consultasNaHora ?? estado.consultasChaveNaHora;
  const limite = estado.limiteConsultasChave;
  const esgotado = usadas >= limite;

  const enviar = () => {
    const valido = consultaChaveRequestSchema.safeParse({ chave });
    if (!valido.success) {
      setErro(valido.error.issues[0]?.message ?? 'Chave de acesso inválida');
      return;
    }
    setErro(undefined);
    setRetorno(null);
    consultar.mutate(valido.data.chave, {
      onSuccess: (resposta) => {
        setRetorno({
          tom: TOM_RESULTADO_CHAVE[resposta.resultado],
          titulo: TITULO_RESULTADO_CHAVE[resposta.resultado],
          mensagem: resposta.mensagem,
          resposta,
        });
      },
      onError: (e) => {
        setRetorno({
          tom: 'alerta',
          titulo: 'A consulta não foi feita',
          mensagem: mensagemErroCaptura(e, estado.fuso),
        });
      },
    });
  };

  return (
    <Dialog
      aberto
      aoMudar={(aberto) => {
        if (!aberto) aoFechar();
      }}
      titulo="Buscar nota pela chave"
      // Largo: a chave inteira (11 blocos) cabe numa linha no desktop.
      className="w-dialogo-largo"
      descricao={`Consulta a SEFAZ como ${estado.empresa.nome}. Notas de até 90 dias; a que já está no FiscoAI não gasta consulta.`}
      acoes={
        <>
          <Button variante="fantasma" onClick={aoFechar}>
            Fechar
          </Button>
          <BotaoComEspera
            type="submit"
            form={idForm}
            variante="primario"
            liberadoEm={estado.bloqueadaAte}
            fuso={estado.fuso}
            motivo={esgotado ? `Limite de ${String(limite)} consultas nesta hora` : null}
            carregando={consultar.isPending}
          >
            Buscar na SEFAZ
          </BotaoComEspera>
        </>
      }
    >
      <form
        id={idForm}
        noValidate
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        <Campo
          rotulo="Chave de acesso"
          erro={erro}
          ajuda="44 posições. Pode colar com espaços."
          complemento={
            <Badge tom={esgotado ? 'alerta' : 'neutro'} className="tabular">
              {`${String(usadas)} de ${String(limite)} nesta hora`}
            </Badge>
          }
        >
          {(a) => (
            <CampoChaveAcesso
              {...a}
              autoFocus
              valor={chave}
              aoMudar={(v) => {
                setChave(v);
                setErro(undefined);
              }}
            />
          )}
        </Campo>
        {retorno && <Resultado r={retorno} />}
      </form>
    </Dialog>
  );
}
